<template>
  <div class="search-container">
    <div class="search-bar">
      <div class="search-icon">
        <!-- Search Icon -->
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width:20px;height:20px"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
      </div>
      <input
        type="text"
        v-model="userInput"
        @keyup.enter="handleSearch"
        placeholder="Query parameters: 'cyberpunk' + 'fps' + '2023'..."
        class="search-input"
        spellcheck="false"
      />
      <button
        @click="handleSearch"
        class="search-button"
        :disabled="isSearching || !userInput.trim()"
      >
        <span v-if="!isSearching">SEARCH</span>
        <span v-else>SEARCHING...</span>
        <span class="button-icon" v-if="!isSearching">
          <!-- ArrowRight Icon -->
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="width:16px;height:16px"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
        </span>
      </button>
    </div>
    
    <!-- Live Token Feedback -->
    <div class="search-tokens" v-if="tokens.length > 0">
      <span v-for="(token, index) in tokens" :key="index" class="token-pill" :class="token.type.toLowerCase()">
        <span class="token-type">{{ token.type }}:</span> {{ token.text }}
      </span>
    </div>

    <!-- Dynamic Suggestions (The new "Try" block) -->
    <div class="search-examples" v-if="suggestions && suggestions.length > 0">
      <span class="examples-label">TRY:</span>
      <button
        v-for="(s, index) in suggestions"
        :key="index"
        @click="selectSuggestion(s)"
        class="example-chip"
        :class="s.type.toLowerCase()"
        :disabled="isSearching"
      >
        {{ s.name }}
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SearchBar',
  data() {
    return {
      suggestions: [], // Dynamic suggestions (Game, Concept, Query)
      tokens: [],
      analysisId: 0 // Track request sequence to prevent stale updates
    };
  },
  mounted() {
    // Small delay to ensure store and state are ready
    setTimeout(() => {
        if (!this.userInput) {
            this.analyzeInput('');
        }
    }, 100);
  },
  watch: {
    userInput(newVal) {
      if (newVal !== undefined) {
        this.analyzeInput(newVal);
      }
    }
  },
  computed: {
    userInput: {
      get() {
        return this.$store.state.userInput;
      },
      set(value) {
        this.$store.commit('setUserInput', value);
      },
    },
    isSearching() {
      return this.$store.state.isLoading;
    },
    knownFilters() {
      return this.$store.state.filters || { genres: [], tags: [], categories: [] };
    },
  },
  methods: {
    handleSearch() {
      if (this.userInput.trim() && !this.isSearching) {
        this.$emit('search', {
          query: this.userInput
        });
      }
    },
    setExample(example) {
      this.userInput = example;
      this.handleSearch();
    },
    selectSuggestion(s) {
      this.userInput = s.name;
      this.handleSearch();
    },
    async analyzeInput(text) {
      const requestId = ++this.analysisId;
      const queryText = (text || '').trim();
      
      if (this.autocompleteTimeout) clearTimeout(this.autocompleteTimeout);
      
      const delay = queryText ? 200 : 0;
      
      this.autocompleteTimeout = setTimeout(async () => {
          const response = await this.$store.dispatch('analyzeQuery', queryText);
          
          if (requestId !== this.analysisId) return;

          if (response) {
              // Update tokens only if we have input
              if (queryText) {
                  if (response.tokens) {
                      const serverTokens = response.tokens;
                      serverTokens.forEach(t => {
                          if (t.type === 'Game' && t.text.length > 25) {
                              t.text = t.text.substring(0, 22) + "...";
                          }
                      });
                      this.tokens = serverTokens.slice(0, 10);
                  }
              } else {
                  this.tokens = []; // Reset tokens for empty query
              }

              if (response.suggestions) {
                  this.suggestions = response.suggestions;
              }
          }
      }, delay);
    },
  },
};
</script>

<style scoped>
.search-container {
  margin-bottom: 32px;
  animation: slideDown 0.4s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.search-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #18181b;
  border: 1px solid #27272a;
  border-radius: 4px; /* Sharp */
  padding: 8px;
  transition: all 0.2s ease;
  margin-bottom: 16px;
}

.search-bar:focus-within {
  background: #18181b;
  border-color: #00dc82;
  box-shadow: 0 4px 0 rgba(0, 220, 130, 0.2); /* Flat shadow instead of glow */
}

.search-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  padding-left: 12px;
  opacity: 0.6;
  transition: opacity 0.3s ease;
  color: #71717a;
}

.search-bar:focus-within .search-icon {
  opacity: 1;
  color: #00dc82;
}

.search-input {
  flex: 1;
  background: transparent;
  border: none;
  color: #ffffff;
  font-size: 1.1rem;
  padding: 12px 8px;
  outline: none;
  font-family: inherit;
  font-weight: 500;
}

.search-input::placeholder {
  color: #52525b;
}

.search-button {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #00dc82;
  color: #000000;
  border: none;
  padding: 12px 24px;
  border-radius: 4px; /* Sharp */
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: inherit;
  white-space: nowrap;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.search-button:disabled {
  background: #27272a;
  color: #52525b;
  cursor: not-allowed;
  transform: none !important;
  box-shadow: none;
}

.search-button:not(:disabled):hover {
  background: #10b981;
  transform: translateY(-2px);
  box-shadow: 0 4px 0 #047857;
}

.search-button:not(:disabled):active {
  transform: translateY(0);
  box-shadow: none;
}

.button-icon {
  display: flex;
  align-items: center;
  transition: transform 0.3s ease;
}

.search-button:not(:disabled):hover .button-icon {
  transform: translateX(4px);
}

.content-filters {
  display: flex;
  gap: 24px;
  padding: 16px;
  background: #121215;
  border-radius: 4px;
  border: 1px solid #27272a;
  margin-bottom: 12px;
  animation: fadeIn 0.5s ease-out 0.1s both;
  flex-wrap: wrap;
}

.filter-checkbox {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  user-select: none;
  transition: opacity 0.2s ease;
}

.filter-checkbox:hover:not(:has(input:disabled)) {
  opacity: 0.8;
}

.filter-checkbox input[type="checkbox"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: #00dc82;
  border-radius: 2px;
  /* Make standard checkboxes square-ish */
}

.filter-checkbox input[type="checkbox"]:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.9rem;
  color: #d1d5db;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.checkbox-icon {
  width: 18px;
  height: 18px;
}

.separator {
  width: 1px;
  background: #27272a;
  height: 24px;
  display: none; /* Hide on mobile/flex-wrap usually */
}
@media (min-width: 768px) {
    .separator { display: block; }
}

/* Filter Info */
.filter-info {
  padding: 10px 14px;
  background: #064e3b;
  border: 1px solid #059669;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.85rem;
  color: #34d399;
  margin-bottom: 12px;
  font-weight: 600;
  text-transform: uppercase;
  animation: slideDown 0.3s ease-out;
}

.info-icon {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
}

.search-examples {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  animation: fadeIn 0.5s ease-out 0.2s both;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.examples-label {
  font-size: 0.85rem;
  color: #71717a;
  font-weight: 700;
  white-space: nowrap;
  text-transform: uppercase;
  margin-right: 4px;
}

.example-chip {
  background: transparent;
  border: 1px solid #3f3f46;
  color: #a1a1aa;
  padding: 6px 14px;
  border-radius: 4px;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: inherit;
  white-space: nowrap;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
}

.example-chip.concept {
  border-color: #a855f7;
  color: #c084fc;
}

.example-chip.game {
  border-color: #06b6d4;
  color: #22d3ee;
}

.chip-prefix {
    opacity: 0.7;
    font-size: 0.8rem;
}

.example-chip:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.example-chip:not(:disabled):hover {
  background: #27272a;
  border-color: #00dc82;
  color: #00dc82;
  transform: translateY(-1px);
}

.example-chip:not(:disabled):active {
  transform: translateY(0);
}

@media (max-width: 768px) {
  .search-bar {
    flex-wrap: wrap;
  }

  .search-icon {
    display: none;
  }

  .search-input {
    width: 100%;
    min-width: 0;
    text-align: left;
    font-size: 1rem;
    padding-left: 12px;
  }

  .search-button {
    width: 100%;
    justify-content: center;
  }

  .content-filters {
    flex-direction: column;
    gap: 12px;
    padding: 16px;
  }

  .filter-checkbox {
    justify-content: flex-start;
  }

  .search-examples {
    justify-content: flex-start;
  }
}

@media (max-width: 480px) {
  .search-input {
    font-size: 0.95rem;
  }

  .search-input::placeholder {
    font-size: 0.85rem;
  }

  .example-chip {
    font-size: 0.8rem;
    padding: 5px 10px;
  }

  .checkbox-label {
    font-size: 0.85rem;
  }

  .filter-info {
    font-size: 0.8rem;
    padding: 8px 12px;
  }
}


.search-tokens {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 20px;
  margin-top: -4px; /* Slightly more space from input */
  padding-left: 2px;
  animation: fadeIn 0.3s ease-out;
  min-height: 28px; /* Ensure space exists even for single line */
}

.token-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #27272a;
  border: 1px solid #3f3f46;
  padding: 3px 8px;
  border-radius: 100px;
  font-size: 0.75rem;
  color: #e4e4e7;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
  white-space: nowrap;
}

.token-pill .token-type {
  color: #71717a;
  font-size: 0.7rem;
  font-weight: 700;
}

/* Specific Token Colors based on type class */
.token-pill.feature { border-color: #3b82f6; }
.token-pill.feature .token-type { color: #60a5fa; }

.token-pill.genre { border-color: #a855f7; }
.token-pill.genre .token-type { color: #c084fc; }

.token-pill.sort { border-color: #eab308; }
.token-pill.sort .token-type { color: #facc15; }

.token-pill.vibe { border-color: #ec4899; }
.token-pill.vibe .token-type { color: #f472b6; }

.token-pill.category { border-color: #10b981; }
.token-pill.category .token-type { color: #34d399; }

.token-pill.concept { border-color: #f59e0b; }
.token-pill.concept .token-type { color: #fbbf24; }

.token-pill.review { border-color: #ef4444; }
.token-pill.review .token-type { color: #f87171; }

.token-pill.year, .token-pill.era { border-color: #64748b; }
.token-pill.year .token-type, .token-pill.era .token-type { color: #94a3b8; }

.token-pill.style { border-color: #8b5cf6; }
.token-pill.style .token-type { color: #a78bfa; }

.token-pill.game { border-color: #06b6d4; }
.token-pill.game .token-type { color: #22d3ee; }

@keyframes slideDown {
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
}
</style>